// src/services/authService.js
// Serviço principal de autenticação para Psiando Global

import supabase from '@/config/supabaseConfig';

// ==================== HELPERS INTERNOS ====================

function getErrorMessage(message) {
  const errorMessages = {
    'Invalid login credentials': 'Email ou senha incorretos',
    'invalid_credentials': 'Email ou senha incorretos',
    'Email not confirmed': 'Email ainda não confirmado. Verifique sua caixa de entrada.',
    'email_not_confirmed': 'Email ainda não confirmado. Verifique sua caixa de entrada.',
    'User already registered': 'Este email já está em uso',
    'Password should be at least 6 characters': 'Senha muito fraca (mínimo 6 caracteres)',
    'Unable to validate email address: invalid format': 'Email inválido',
    'Too many requests': 'Muitas tentativas. Tente novamente mais tarde',
    'too_many_requests': 'Muitas tentativas. Tente novamente mais tarde',
  };
  return errorMessages[message] || 'Erro inesperado. Tente novamente.';
}

// ==================== CRIAÇÃO DE PERFIL ====================

/**
 * Garante que o perfil existe na tabela `profiles`.
 *
 * FIX: Trocado insert() por upsert() com ignoreDuplicates: true para evitar
 * race condition quando dois eventos (ex: login + onAuthStateChange) chamam
 * esta função ao mesmo tempo e ambos passam pelo select antes do insert
 * completar, resultando em erro de chave duplicada.
 *
 * Schema real: id, name, email, avatar_url, slug, user_type, status,
 * crp, specialties, bio, location, phone, website, is_admin,
 * profile_complete, accepts_online, accepts_presential,
 * consultation_price, supervision_price, currency, country_code,
 * timezone, language, created_at, updated_at
 */
async function createProfileIfNotExists(user) {
  if (!user?.id) return;

  try {
    const metadata = user.user_metadata || {};
    const userType = metadata.user_type || null;

    // Verificar se o perfil já existe
    const { data: existing } = await supabase
      .from('profiles')
      .select('id, user_type')
      .eq('id', user.id)
      .maybeSingle();

    if (existing) {
      // Perfil existe — só atualizar user_type se ainda estiver nulo
      // (evita sobrescrever escolha feita pelo trigger ou pelo usuário)
      if (!existing.user_type && userType) {
        await supabase
          .from('profiles')
          .update({ user_type: userType, updated_at: new Date().toISOString() })
          .eq('id', user.id);
      }
      return;
    }

    // Perfil não existe — criar (trigger pode ter falhado)
    const { error: insertError } = await supabase
      .from('profiles')
      .insert({
        id: user.id,
        email: user.email,
        name: metadata.name || metadata.full_name || user.email.split('@')[0],
        user_type: userType || 'psicologo',
        status: 'active',
        crp: metadata.crp || null,
        specialties: metadata.specialties || [],
        location: metadata.location || '',
        phone: metadata.phone || '',
        is_admin: false,
        profile_complete: false,
        accepts_online: false,
        accepts_presential: false,
        timezone: 'America/Sao_Paulo',
        language: 'pt-BR',
        currency: 'BRL',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });

    if (insertError) {
      console.error('[authService] Erro ao criar perfil:', insertError.message);
    }
  } catch (err) {
    console.error('[authService] Falha em createProfileIfNotExists:', err.message);
  }
}

// ==================== AUTENTICAÇÃO ====================

async function login(email, password) {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    await createProfileIfNotExists(data.user);
    return { success: true, user: data.user, session: data.session, message: 'Login realizado com sucesso!' };
  } catch (error) {
    return { success: false, error: getErrorMessage(error.message), code: error.message };
  }
}

async function register(email, password, userData = {}) {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name: userData.fullName || userData.name || '',
          user_type: userData.userType || null,
          crp: userData.crp || null,
          specialties: userData.specialties || [],
          location: userData.location || '',
          phone: userData.phone || '',
        },
      },
    });
    if (error) throw error;
    if (data.user) await createProfileIfNotExists(data.user);
    return { success: true, user: data.user, session: data.session, message: 'Conta criada! Verifique seu email.' };
  } catch (error) {
    return { success: false, error: getErrorMessage(error.message), code: error.message };
  }
}

async function loginWithGoogle() {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: getErrorMessage(error.message), code: error.message };
  }
}

async function loginWithFacebook() {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'facebook',
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: getErrorMessage(error.message), code: error.message };
  }
}

// ==================== PERFIL ====================

async function getUserData(uid) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', uid)
      .maybeSingle();
    if (error) throw error;
    if (!data) return { success: false, error: 'Perfil não encontrado.' };
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// Campos permitidos para update (exatamente o que existe no banco)
const ALLOWED_PROFILE_FIELDS = [
  'name', 'avatar_url', 'slug', 'user_type', 'status', 'crp',
  'specialties', 'bio', 'location', 'phone', 'website',
  'profile_complete', 'accepts_online', 'accepts_presential',
  'consultation_price', 'supervision_price', 'currency',
  'country_code', 'timezone', 'language',
];

async function updateUserProfile(updates) {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('Usuário não autenticado.');

    await createProfileIfNotExists(user);

    const safeUpdates = Object.fromEntries(
      Object.entries(updates).filter(([key]) => ALLOWED_PROFILE_FIELDS.includes(key))
    );

    const { data, error } = await supabase
      .from('profiles')
      .update({ ...safeUpdates, updated_at: new Date().toISOString() })
      .eq('id', user.id)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data, message: 'Perfil atualizado com sucesso!' };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// ==================== SENHA / LOGOUT ====================

async function logout() {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    return { success: true, message: 'Logout realizado com sucesso!' };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

async function resetPassword(email) {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });
    if (error) throw error;
    return { success: true, message: 'Email de recuperação enviado!' };
  } catch (error) {
    return { success: false, error: getErrorMessage(error.message) };
  }
}

async function updatePassword(newPassword) {
  try {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    return { success: true, message: 'Senha atualizada com sucesso!' };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// ==================== EXPORTAÇÕES ====================

const authService = {
  login, register, loginWithGoogle, loginWithFacebook,
  getUserData, updateUserProfile, createProfileIfNotExists,
  logout, resetPassword, updatePassword,
};

export { createProfileIfNotExists, authService };
export default authService;
